import { useEffect, useRef, useState } from 'react';

import classNames from '@rio-cloud/rio-uikit/classNames';
import RadioButton from '@rio-cloud/rio-uikit/RadioButton';

import AnimationDelayControl from './controls/AnimationDelayControl';
import AnimationDurationControl from './controls/AnimationDurationControl';
import AnimationEasingControl from './controls/AnimationEasingControl';
import AnimationLoopingControl from './controls/AnimationLoopingControl';
import AnimationPlaybackControl from './controls/AnimationPlaybackControl';
import AnimationRepeatControl from './controls/AnimationRepeatControl';
import type {
    AnimationDelay,
    AnimationDuration,
    AnimationEasing,
    AnimationLooping,
    AnimationRepeat,
} from './controls/animationControlOptions';

const durationToCssValue = (duration: AnimationDuration) => {
    if (duration.startsWith('0') && duration.length === 3) {
        return `0.${duration[1]}s`;
    }

    return duration;
};

const rotateClassToDegrees = (rotateClass: string) => Number(rotateClass.replace('rotate-', ''));

const transformOriginClassToCssValue = (originClass: string) =>
    originClass.replace('transform-origin-', '').replaceAll('-', ' ');

const repeatToIterations = (repeat: AnimationRepeat) => {
    switch (repeat) {
        case 'infinite':
            return 'infinite';
        case 'repeat-2':
            return 2;
        case 'repeat-3':
            return 3;
        default:
            return 1;
    }
};

const AnimationsRotateExample = () => {
    const [currentRotateClass, setCurrentRotateClass] = useState('rotate-360');
    const [currentOriginClass, setCurrentOriginClass] = useState('transform-origin-center');
    const [currentAnimationDelay, setCurrentAnimationDelay] = useState<AnimationDelay>('01s');
    const [currentAnimationDuration, setCurrentAnimationDuration] = useState<AnimationDuration>('1s');
    const [currentAnimationEasing, setCurrentAnimationEasing] = useState<AnimationEasing>('ease-in-out');
    const [currentAnimationLooping, setCurrentAnimationLooping] = useState<AnimationLooping>('one-time');
    const [currentAnimationRepeat, setCurrentAnimationRepeat] = useState<AnimationRepeat>('no-repeat');
    const [playAnimation, setPlayAnimation] = useState(false);

    const animationRef = useRef<HTMLDivElement>(null);
    const currentDegrees = rotateClassToDegrees(currentRotateClass);
    const currentDelay = durationToCssValue(currentAnimationDelay);
    const currentDuration = durationToCssValue(currentAnimationDuration);
    const currentTransformOrigin = transformOriginClassToCssValue(currentOriginClass);
    const repeatIterations = repeatToIterations(currentAnimationRepeat);

    useEffect(() => {
        const animationElement = animationRef.current;

        if (!animationElement) {
            return;
        }

        animationElement.getAnimations().forEach(animation => {
            animation.cancel();
        });
        animationElement.style.removeProperty('transform');

        if (!playAnimation) {
            return;
        }

        const loopIterations =
            repeatIterations === 'infinite'
                ? 'infinite'
                : currentAnimationLooping === 'loop'
                  ? repeatIterations * 2
                  : repeatIterations;

        const animation = animationElement.animate(
            [{ transform: 'rotate(0deg)' }, { transform: `rotate(${currentDegrees}deg)` }],
            {
                delay: Number.parseFloat(currentDelay) * 1000,
                duration: Number.parseFloat(currentDuration) * 1000,
                easing: currentAnimationEasing,
                iterations: loopIterations === 'infinite' ? Number.POSITIVE_INFINITY : loopIterations,
                direction: currentAnimationLooping === 'loop' ? 'alternate' : 'normal',
                fill: currentAnimationLooping === 'restart' ? 'none' : 'both',
            }
        );

        return () => animation.cancel();
    }, [
        playAnimation,
        currentDegrees,
        currentDelay,
        currentDuration,
        currentAnimationEasing,
        currentAnimationLooping,
        repeatIterations,
    ]);

    const animationClassName = classNames(playAnimation && currentOriginClass);
    const animationCode = classNames(
        `rotate(${currentDegrees}deg)`,
        currentOriginClass,
        `animated-delay-${currentAnimationDelay}`,
        `animated-${currentAnimationDuration}`,
        currentAnimationRepeat,
        currentAnimationEasing,
        currentAnimationLooping
    );

    return (
        <>
            <style>
                {`
                    .rotate-example-target {
                        transform: rotate(0deg);
                    }
                `}
            </style>
            <div className='display-flex flex-wrap justify-content-center'>
                {['0', '45', '90', '135', '180', '225', '270', '315', '360', '1', '5', '-1', '-5'].map(angle => (
                    <RadioButton
                        key={angle}
                        name='rotate-value'
                        onChange={() => {
                            setCurrentRotateClass(`rotate-${angle}`);
                            setPlayAnimation(false);
                        }}
                        checked={currentRotateClass === `rotate-${angle}`}
                        inline
                    >
                        {angle}deg
                    </RadioButton>
                ))}
            </div>
            <hr />
            <div className='display-flex flex-wrap justify-content-center'>
                {[
                    'transform-origin-center',
                    'transform-origin-top',
                    'transform-origin-top-left',
                    'transform-origin-top-right',
                    'transform-origin-right',
                    'transform-origin-bottom',
                    'transform-origin-left',
                ].map(origin => (
                    <RadioButton
                        key={origin}
                        name='rotate-origin'
                        onChange={() => {
                            setCurrentOriginClass(origin);
                            setPlayAnimation(false);
                        }}
                        checked={currentOriginClass === origin}
                        inline
                    >
                        {origin.replace('transform-origin-', '')}
                    </RadioButton>
                ))}
            </div>
            <hr />
            <AnimationDurationControl
                value={currentAnimationDuration}
                onChange={value => {
                    if (!value) {
                        return;
                    }

                    setCurrentAnimationDuration(value);
                    setPlayAnimation(false);
                }}
            />
            <hr />
            <AnimationDelayControl
                value={currentAnimationDelay}
                onChange={value => {
                    if (!value) {
                        return;
                    }

                    setCurrentAnimationDelay(value);
                    setPlayAnimation(false);
                }}
            />
            <hr />
            <AnimationEasingControl
                value={currentAnimationEasing}
                onChange={value => {
                    if (!value) {
                        return;
                    }

                    setCurrentAnimationEasing(value);
                    setPlayAnimation(false);
                }}
            />
            <hr />
            <AnimationRepeatControl
                value={currentAnimationRepeat}
                onChange={value => {
                    if (!value) {
                        return;
                    }

                    setCurrentAnimationRepeat(value);
                    setPlayAnimation(false);
                }}
            />
            <hr />
            <AnimationLoopingControl
                value={currentAnimationLooping}
                onChange={value => {
                    if (!value) {
                        return;
                    }

                    setCurrentAnimationLooping(value);
                    setPlayAnimation(false);
                }}
            />
            <hr />
            <AnimationPlaybackControl
                isPlaying={playAnimation}
                onPlay={() => setPlayAnimation(true)}
                onReset={() => setPlayAnimation(false)}
            />
            <hr />
            <div className='display-flex flex-column align-items-center justify-content-center height-300'>
                <div
                    ref={animationRef}
                    className={classNames('rotate-example-target', animationClassName)}
                    style={{ transformOrigin: currentTransformOrigin }}
                >
                    <svg
                        className='margin-5 margin-left-0 scale-200'
                        width='50'
                        height='50'
                        xmlns='http://www.w3.org/2000/svg'
                    >
                        <path
                            d={
                                'M0 50V0h50v50H0zm45.3-23.95c0-4.55-3.7-8.25-8.25-8.25s-8.25 ' +
                                '3.7-8.25 8.25 3.7 8.25 8.25 8.25 8.25-3.7 8.25-8.25zm-23.6 7.' +
                                '8h5.05V18.2H21.7v15.65zm-8-15.65H5.25v15.65h4.9V27.6l4.25 ' +
                                '6.25h5.35l-4.1-6.25c.45-.15.85-.4 1.15-.7 1.1-.9 1.8-2.3 1.8-3.8 ' +
                                '0-2.7-2.2-4.9-4.9-4.9z'
                            }
                            fillRule='evenodd'
                        />
                    </svg>
                </div>
            </div>
            <hr />
            <div className='display-flex justify-content-center'>
                <code className='code hidden-empty'>{animationCode}</code>
            </div>
        </>
    );
};

export default AnimationsRotateExample;
